import {Menu} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import React from 'react';
import type {NotificationRuleSimpleResponse} from 'src/api/ProactApiClient';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useNotificationRulesColumns} from 'src/app/notification-rules/NotificationRulesTable/hooks/useNotificationRulesColumns';
import {useGetNotificationRuleListV2Query} from 'src/redux/queries/notificationRule';

export interface NotificationRulesTableProps {
  identifier: string;
  actionBarPreNode?: React.ReactNode;
  actionBarPostNode?: React.ReactNode;
  onViewRowClick: (row: NotificationRuleSimpleResponse) => void;
}

export const NotificationRulesTable: React.FC<NotificationRulesTableProps> = ({
  identifier,
  actionBarPreNode,
  actionBarPostNode,
  onViewRowClick,
}) => {
  const columns = useNotificationRulesColumns();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetNotificationRuleListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => onViewRowClick(row.original)}>
            View
          </Menu.Item>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <Group justify="space-between" gap={3}>
          {actionBarPreNode}
          {actionBarPostNode}
        </Group>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
